import { ComponentType, ReactNode, useEffect, useMemo, useState, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Filter, Search, CheckCircle2, X } from "lucide-react";
import {
  FilterPanel,
  FilterSection,
  FilterField,
  FilterFieldButton,
} from "../Filters/common/FilterPanel.tsx";
import styles from "./WorkOffersFilters.module.scss";

export interface WorkOffersFilterState {
  search: string;
  skills: string[];
  zones: string[];
}

interface WorkOffersFiltersProps {
  skills: string[];
  zones: string[];
  value: WorkOffersFilterState;
  onChange: (next: WorkOffersFilterState) => void;
  totalCount: number;
}

const Presence = AnimatePresence as unknown as ComponentType<{ children: ReactNode; initial?: boolean }>;

const WorkOffersFilters = ({ skills, zones, value, onChange, totalCount }: WorkOffersFiltersProps) => {
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState<"skills" | "zones" | null>(null);
  const mobileRef = useRef<HTMLDivElement | null>(null);

  const skillOptions = useMemo(() => {
    const map = new Map<string, number>();
    skills.forEach((skill) => {
      const key = skill.trim();
      if (!key) return;
      map.set(key, (map.get(key) ?? 0) + 1);
    });
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name]) => name);
  }, [skills]);

  const zoneOptions = useMemo(() => {
    const map = new Map<string, number>();
    zones.forEach((zone) => {
      const key = zone.trim();
      if (!key) return;
      map.set(key, (map.get(key) ?? 0) + 1);
    });
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([label]) => label);
  }, [zones]);

  const filterCount = useMemo(() => {
    let count = 0;
    if (value.search.trim().length > 0) count++;
    if (value.skills.length > 0) count++;
    if (value.zones.length > 0) count++;
    return count;
  }, [value]);

  const toggleSkill = (skill: string) => {
    const hasSkill = value.skills.includes(skill);
    const next = hasSkill ? value.skills.filter((item) => item !== skill) : [...value.skills, skill];
    onChange({ ...value, skills: next });
  };

  const toggleZone = (zone: string) => {
    const hasZone = value.zones.includes(zone);
    const next = hasZone ? value.zones.filter((item) => item !== zone) : [...value.zones, zone];
    onChange({ ...value, zones: next });
  };

  const resetFilters = () => {
    onChange({ search: "", skills: [], zones: [] });
    setSelectedFilter(null);
    setMobileOpen(false);
    setMobileSection(null);
  };

  useEffect(() => {
    if (mobileOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      const handleClickOutside = (event: MouseEvent) => {
        if (mobileRef.current && !mobileRef.current.contains(event.target as Node)) {
          setMobileOpen(false);
          setMobileSection(null);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.body.style.overflow = originalOverflow;
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
    return undefined;
  }, [mobileOpen]);

  const summary =
    value.skills.length === 0 && value.zones.length === 0 ? null : (
      <div className={styles.chipSummary}>
        {value.skills.map((skill) => (
          <span key={`skill-${skill}`} className={styles.chipSummaryItem}>
            <CheckCircle2 size={14} />
            {skill}
          </span>
        ))}
        {value.zones.map((zone) => (
          <span key={`zone-${zone}`} className={styles.chipSummaryItem}>
            <CheckCircle2 size={14} />
            {zone}
          </span>
        ))}
      </div>
    );

  return (
    <div className={styles.root}>
      <div className={styles.desktop}>
        <FilterPanel
          title="Filtros"
          filterCount={filterCount}
          onReset={resetFilters}
          summary={summary}
          primary={
            <>
              <FilterSection>
                <FilterField>
                  <div className={styles.searchInputWrapper}>
                    <Search className={styles.searchIcon} size={16} />
                    <input
                      className={styles.searchInput}
                      type="text"
                      value={value.search}
                      onChange={(event) => onChange({ ...value, search: event.target.value })}
                      placeholder="Buscar por rubro, empresa, ubicación…"
                    />
                  </div>
                </FilterField>
              </FilterSection>

              <FilterSection>
                <FilterField>
                  <FilterFieldButton
                    title="Rubros"
                    value={
                      value.skills.length === 0
                        ? undefined
                        : value.skills.length === 1
                          ? value.skills[0]
                          : `${value.skills.length} seleccionados`
                    }
                    placeholder="Todos"
                    onClick={() => setSelectedFilter((prev) => (prev === "skills" ? null : "skills"))}
                    active={selectedFilter === "skills"}
                  />
                  {selectedFilter === "skills" && (
                    <div className={styles.dropdownPanel}>
                      {skillOptions.length === 0 ? (
                        <div className={styles.dropdownEmpty}>No hay rubros disponibles</div>
                      ) : (
                        <div className={styles.dropdownList}>
                          {skillOptions.map((skill) => (
                            <button
                              key={skill}
                              type="button"
                              className={styles.dropdownItem}
                              data-active={value.skills.includes(skill)}
                              onClick={() => toggleSkill(skill)}
                            >
                              {skill}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </FilterField>
              </FilterSection>

              <FilterSection>
                <FilterField>
                  <FilterFieldButton
                    title="Ubicación"
                    value={
                      value.zones.length === 0
                        ? undefined
                        : value.zones.length === 1
                          ? value.zones[0]
                          : `${value.zones.length} seleccionadas`
                    }
                    placeholder="Todas"
                    onClick={() => setSelectedFilter((prev) => (prev === "zones" ? null : "zones"))}
                    active={selectedFilter === "zones"}
                  />
                  {selectedFilter === "zones" && (
                    <div className={styles.dropdownPanel}>
                      {zoneOptions.length === 0 ? (
                        <div className={styles.dropdownEmpty}>No hay ubicaciones disponibles</div>
                      ) : (
                        <div className={styles.dropdownList}>
                          {zoneOptions.map((zone) => (
                            <button
                              key={zone}
                              type="button"
                              className={styles.dropdownItem}
                              data-active={value.zones.includes(zone)}
                              onClick={() => toggleZone(zone)}
                            >
                              {zone}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </FilterField>
              </FilterSection>

            </>
          }
          onToggleAdvanced={() => {}}
          advancedOpen={false}
        />
      </div>
      <div className={styles.mobile}>
        <div className={styles.mobileTriggerBar}>
          <button type="button" className={styles.mobileTrigger} onClick={() => setMobileOpen(true)}>
            <Filter size={18} />
            Filtros
            {filterCount > 0 && <span className={styles.mobileBadge}>{filterCount}</span>}
          </button>
          <div className={styles.mobileSummary}>
            <span className={styles.mobileSummaryCount}>{totalCount}</span>
            <span className={styles.mobileSummaryText}>
              {totalCount === 1 ? "oferta" : "ofertas"}
            </span>
          </div>
        </div>

        <Presence initial={false}>
          {mobileOpen && (
            <motion.div
              className={styles.mobileOverlay}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                ref={mobileRef}
                className={styles.mobileSheet}
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "100%" }}
                transition={{ type: "spring", damping: 26, stiffness: 280 }}
              >
                <header className={styles.mobileHeader}>
                  <div>
                    <h3>Filtros</h3>
                    <p>{filterCount === 0 ? "Sin filtros activos" : `${filterCount} filtros activos`}</p>
                  </div>
                  <button
                    type="button"
                    className={styles.mobileClose}
                    onClick={() => {
                      setMobileOpen(false);
                      setMobileSection(null);
                    }}
                    aria-label="Cerrar filtros"
                  >
                    <X size={20} />
                  </button>
                </header>

                <div className={styles.mobileContent}>
                  <div className={styles.mobileSearch}>
                    <Search size={16} />
                    <input
                      type="text"
                      value={value.search}
                      onChange={(event) => onChange({ ...value, search: event.target.value })}
                      placeholder="Buscar por rubro, empresa, ubicación…"
                    />
                  </div>

                  <FilterFieldButton
                    title="Rubros"
                    value={
                      value.skills.length === 0
                        ? "Todos"
                        : value.skills.length === 1
                          ? value.skills[0]
                          : `${value.skills.length} seleccionados`
                    }
                    onClick={() => setMobileSection((prev) => (prev === "skills" ? null : "skills"))}
                    active={mobileSection === "skills"}
                  />
                  <Presence initial={false}>
                    {mobileSection === "skills" && (
                      <motion.div
                        className={styles.mobileDropdown}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                      >
                        {skillOptions.length === 0 ? (
                          <div className={styles.dropdownEmpty}>No hay rubros disponibles</div>
                        ) : (
                          <div className={styles.mobileChips}>
                            {skillOptions.map((skill) => {
                              const isActive = value.skills.includes(skill);
                              return (
                                <button
                                  key={skill}
                                  type="button"
                                  className={styles.mobileChip}
                                  data-active={isActive}
                                  onClick={() => toggleSkill(skill)}
                                >
                                  {skill}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </motion.div>
                    )}
                  </Presence>

                  <FilterFieldButton
                    title="Ubicación"
                    value={
                      value.zones.length === 0
                        ? "Todas"
                        : value.zones.length === 1
                          ? value.zones[0]
                          : `${value.zones.length} seleccionadas`
                    }
                    onClick={() => setMobileSection((prev) => (prev === "zones" ? null : "zones"))}
                    active={mobileSection === "zones"}
                  />
                  <Presence initial={false}>
                    {mobileSection === "zones" && (
                      <motion.div
                        className={styles.mobileDropdown}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                      >
                        {zoneOptions.length === 0 ? (
                          <div className={styles.dropdownEmpty}>No hay ubicaciones disponibles</div>
                        ) : (
                          <div className={styles.mobileChips}>
                            {zoneOptions.map((zone) => {
                              const isActive = value.zones.includes(zone);
                              return (
                                <button
                                  key={zone}
                                  type="button"
                                  className={styles.mobileChip}
                                  data-active={isActive}
                                  onClick={() => toggleZone(zone)}
                                >
                                  {zone}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </motion.div>
                    )}
                  </Presence>
                </div>

                <footer className={styles.mobileFooter}>
                  <button type="button" className={styles.mobileReset} onClick={resetFilters}>
                    Limpiar filtros
                  </button>
                </footer>
              </motion.div>
            </motion.div>
          )}
        </Presence>
      </div>
    </div>
  );
};

export default WorkOffersFilters;

